import moment from "moment";
import { useCallback, useMemo } from "react";
import { MeetingsOverviewMeetingFragment } from "types/graphql-schema";

import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import MotionDiv from "@components/motion/motion-div";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getUrl, isTailwindLgActive } from "@helpers/helpers";

const MeetingIcon = getTopicflowIcon("meeting");

const OverviewMeeting = ({
  meeting,
  onClickMeetingLink,
  compact = false,
}: {
  meeting: MeetingsOverviewMeetingFragment;
  hideEditingButtons?: boolean;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
}) => {
  const link = useLink();

  const meetingUrl = useMemo(
    () =>
      getUrl({
        meetingGroupId: meeting.meetingGroupId,
        meetingId: meeting.id,
      }),
    [meeting],
  );

  const handleGoToMeeting = useCallback(() => {
    link.redirect(meetingUrl);
  }, [link, meetingUrl]);

  const handleClickMeetingLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
      if (onClickMeetingLink && meeting.meetingGroup?.id) {
        if (isTailwindLgActive()) {
          e.stopPropagation();
          e.preventDefault();
          onClickMeetingLink(meeting.id, meeting.meetingGroup.id);
        } else {
          handleGoToMeeting();
        }
      }
    },
    [
      meeting.id,
      onClickMeetingLink,
      meeting.meetingGroup?.id,
      handleGoToMeeting,
    ],
  );

  return (
    <>
      <MotionDiv
        className={classNames(
          "text-sm text-gray-600 rounded-md px-2 -mx-2 py-1 hover:bg-nav-item-active",
          window.location.pathname === meetingUrl && "bg-nav-item-active",
        )}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        role="button"
        onClick={onClickMeetingLink ? handleClickMeetingLink : undefined}
      >
        <AppLink
          className={classNames("flex items-center gap-2 w-full min-w-0")}
          to={meetingUrl}
          onClick={handleClickMeetingLink}
        >
          {compact && (
            <MeetingIcon className="h-4 w-4 text-gray-500 shrink-0" />
          )}
          <span className="flex-1 truncate">{meeting.title}</span>
          <span className="text-gray-500 text-xs">
            {`${moment(meeting.startDatetime)
              .format("h:mma")
              .replace(":00", "")}`}
          </span>
        </AppLink>
      </MotionDiv>
    </>
  );
};

export default OverviewMeeting;
